import { A, useLocation } from '@solidjs/router'
import {
  IconChevronDown,
  IconChevronRight,
  IconDeviceMobile,
  IconInbox,
  IconLayoutSidebar,
  IconLogout,
  IconMenu2,
  IconSearch,
  IconSparkles,
} from '@tabler/icons-solidjs'
import { can } from '@veem/crm-schema'
import { cn, Kbd, Sheet, showToast } from '@veem/ui'
import {
  createEffect,
  createMemo,
  createSignal,
  For,
  type JSX,
  onCleanup,
  onMount,
  type ParentProps,
  Show,
} from 'solid-js'
import { session, signOut } from '../../lib/session.ts'
import { personById } from '../data/people.ts'
import { formatCount } from '../lib/format.ts'
import { allNavItems, BASE, navFor, type ProtoNavItem } from '../lib/nav.tsx'
import { inboxCount } from '../state/inbox.ts'
import { newInboxCount, useInboxNews } from '../state/inbox-news.ts'
import { proto } from '../state/store.ts'
import { AssistantDock } from './assistant.tsx'
import { CommandSearch } from './command-search.tsx'
import { Avatar, MenuRow, PopoverPanel, usePopover } from './primitives.tsx'
import { PushSettings } from './push-settings.tsx'

const [paletteOpen, setPaletteOpen] = createSignal(false)
const [assistantOpen, setAssistantOpen] = createSignal(false)
const [collapsed, setCollapsed] = createSignal(false)
const [mobileNavOpen, setMobileNavOpen] = createSignal(false)
const [pushOpen, setPushOpen] = createSignal(false)

export const openSearch = () => setPaletteOpen(true)
export const openAssistant = () => {
  const composer = document.querySelector<HTMLTextAreaElement>('[data-ai-home-composer] textarea')
  if (composer) composer.focus()
  else setAssistantOpen(true)
}

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)
const MOD = isMac ? '⌘' : 'Ctrl'

const rowClass = (active: boolean) =>
  cn(
    'group flex h-8 w-full items-center gap-2.5 rounded-control px-2 text-body font-medium max-lg:h-(--size-touch)',
    'transition-[background-color,color,box-shadow] duration-(--duration-press) ease-out',
    'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent',
    '[&_svg]:size-4 [&_svg]:shrink-0',
    active
      ? 'bg-surface text-text shadow-[0_0_0_1px_var(--color-border),0_1px_2px_oklch(0.196_0.007_265/0.06)] [&_svg]:text-accent'
      : 'text-text-muted hover:bg-surface-sunken/70 hover:text-text [&_svg]:text-text-subtle',
  )

const NavLink = (props: {
  item: ProtoNavItem
  active: boolean
  compact: boolean
  count?: number
}) => {
  let link: HTMLAnchorElement | undefined
  // A long menu scrolls; bring the current section into view whenever it changes, so the
  // highlight is never hidden above or below the fold.
  createEffect(() => {
    if (props.active) link?.scrollIntoView({ block: 'nearest' })
  })
  return (
    <A
      ref={link}
      href={props.item.href}
      class={cn(rowClass(props.active), props.compact && 'justify-center px-0')}
      aria-current={props.active ? 'page' : undefined}
      title={props.compact ? props.item.label : undefined}
      onClick={() => setMobileNavOpen(false)}
    >
      {props.item.icon()}
      <Show when={!props.compact}>
        <span class="min-w-0 flex-1 truncate">{props.item.label}</span>
        <Show when={props.count}>
          <span class="font-mono text-micro text-text-subtle tabular-nums">{props.count}</span>
        </Show>
      </Show>
    </A>
  )
}

const UserMenu = (props: { compact: boolean }) => {
  const popover = usePopover()
  const person = () => personById(proto.personId())
  const logout = async () => {
    try {
      await signOut()
    } catch (cause) {
      showToast(cause instanceof Error ? cause.message : 'Не вдалося вийти', 'error')
    }
  }
  return (
    <div class="relative" ref={popover.ref}>
      <button
        type="button"
        onClick={() => popover.setOpen(!popover.open())}
        aria-expanded={popover.open()}
        aria-label="Меню користувача"
        class={cn(rowClass(false), 'h-10 text-text', props.compact && 'justify-center px-0')}
      >
        <Avatar name={person().name} size="sm" />
        <Show when={!props.compact}>
          <span class="grid min-w-0 flex-1 text-left">
            <span class="truncate text-body font-semibold">{person().name}</span>
            <span class="truncate text-caption font-normal text-text-subtle">{person().title}</span>
          </span>
        </Show>
      </button>
      <Show when={popover.open()}>
        <PopoverPanel class="bottom-full left-0 mb-1 w-60">
          <p class="px-2 py-1 text-caption text-text-subtle">{session()?.user.email}</p>

          <div class="my-1 border-t border-border" />
          <MenuRow
            icon={<IconDeviceMobile stroke-width={1.5} />}
            onClick={() => {
              popover.setOpen(false)
              setMobileNavOpen(false)
              setPushOpen(true)
            }}
          >
            Сповіщення на телефон
          </MenuRow>
          <MenuRow icon={<IconLogout stroke-width={1.5} />} onClick={logout}>
            Вийти
          </MenuRow>
        </PopoverPanel>
      </Show>
    </div>
  )
}

/**
 * Collapsed sidebar sections, remembered per person in this browser, so each user keeps the
 * menu the way they left it.
 */
const COLLAPSED_KEY = 'veem-proto-nav-collapsed'
const readCollapsed = (): Record<string, string[]> => {
  try {
    return JSON.parse(localStorage.getItem(COLLAPSED_KEY) ?? '{}') as Record<string, string[]>
  } catch {
    return {}
  }
}
const [collapsedByPerson, setCollapsedByPerson] = createSignal(readCollapsed())
const collapsedGroups = () => collapsedByPerson()[proto.personId()] ?? []
const toggleGroup = (label: string) => {
  const current = collapsedGroups()
  const next = {
    ...collapsedByPerson(),
    [proto.personId()]: current.includes(label)
      ? current.filter((item) => item !== label)
      : [...current, label],
  }
  setCollapsedByPerson(next)
  localStorage.setItem(COLLAPSED_KEY, JSON.stringify(next))
}

const SidebarContent = (props: { compact: boolean }) => {
  const location = useLocation()
  const groups = createMemo(() => navFor(proto.role()))
  const count = createMemo(() => inboxCount(proto.role(), proto.personId()))
  const fresh = createMemo(newInboxCount)
  /**
   * The longest matching address selects the current section. Material pages select stock.
   */
  const activeHref = createMemo(() => {
    const path = location.pathname
    const hrefs = [`${BASE}/home`, `${BASE}/inbox`, ...allNavItems().map((item) => item.href)]
    return hrefs
      .filter((href) => path === href || path.startsWith(`${href}/`))
      .sort((left, right) => right.length - left.length)[0]
  })
  const isActive = (href: string) => activeHref() === href

  return (
    <div class="flex h-full min-h-0 flex-col gap-3">
      <div class={cn('flex items-center gap-2 px-1 pt-1', props.compact && 'flex-col')}>
        <span
          class="grid size-7 shrink-0 place-items-center rounded-control bg-ink font-display text-[0.625rem] font-bold text-text-inverse"
          aria-hidden="true"
        >
          АМ
        </span>
        <Show when={!props.compact}>
          <span class="min-w-0 flex-1 truncate font-display text-caption font-bold tracking-tight text-text">
            АМ-АВТОПРОМ
          </span>
        </Show>
        <button
          type="button"
          onClick={() => setCollapsed(!collapsed())}
          aria-label={collapsed() ? 'Розгорнути меню' : 'Згорнути меню'}
          title={collapsed() ? 'Розгорнути меню' : 'Згорнути меню'}
          class="grid size-7 place-items-center rounded-control text-text-subtle hover:bg-surface-sunken hover:text-text focus-visible:outline-2 focus-visible:outline-accent max-lg:hidden"
        >
          <IconLayoutSidebar class="size-4" stroke-width={1.5} aria-hidden="true" />
        </button>
      </div>

      <div class="grid gap-0.5">
        <button
          type="button"
          onClick={() => setPaletteOpen(true)}
          class={cn(rowClass(false), props.compact && 'justify-center px-0')}
          title={props.compact ? 'Пошук' : undefined}
        >
          <IconSearch stroke-width={1.5} />
          <Show when={!props.compact}>
            <span class="flex-1 text-left">Пошук</span>
            <Kbd>{MOD} K</Kbd>
          </Show>
        </button>
        <A
          href={`${BASE}/home`}
          class={cn(rowClass(isActive(`${BASE}/home`)), props.compact && 'justify-center px-0')}
          aria-current={isActive(`${BASE}/home`) ? 'page' : undefined}
          title={props.compact ? 'Помічник' : undefined}
          onClick={() => {
            setMobileNavOpen(false)
            if (location.pathname === `${BASE}/home`) openAssistant()
          }}
        >
          <IconSparkles stroke-width={1.5} />
          <Show when={!props.compact}>
            <span class="flex-1 text-left">Помічник</span>
            <Kbd>{MOD} J</Kbd>
          </Show>
        </A>
        <A
          href={`${BASE}/inbox`}
          class={cn(rowClass(isActive(`${BASE}/inbox`)), props.compact && 'justify-center px-0')}
          aria-current={isActive(`${BASE}/inbox`) ? 'page' : undefined}
          title={props.compact ? 'Мої завдання' : undefined}
          onClick={() => setMobileNavOpen(false)}
        >
          <IconInbox stroke-width={1.5} />
          <Show when={!props.compact}>
            <span class="flex-1">Мої завдання</span>
          </Show>
          <Show when={fresh() > 0}>
            <span
              data-inbox-new-badge
              title={`Нових: ${formatCount(fresh())}`}
              class={cn(
                'grid h-5 min-w-5 place-items-center rounded-full bg-accent px-1.5 font-mono text-micro font-semibold text-text-inverse tabular-nums',
                props.compact && 'absolute ml-5 -mt-4 h-4 min-w-4 px-1',
              )}
            >
              {formatCount(fresh())}
            </span>
          </Show>
          <Show when={fresh() === 0 && count() > 0 && !props.compact}>
            <span
              data-inbox-count
              class="font-mono text-micro text-text-subtle tabular-nums"
              title={`Усього: ${formatCount(count())}`}
            >
              {formatCount(count())}
            </span>
          </Show>
        </A>
      </div>

      <nav
        aria-label="Розділи"
        class="proto-quiet-scroll -mx-1 grid min-h-0 flex-1 content-start gap-4 overflow-y-auto px-1 pt-2 pb-4"
      >
        <For each={groups()}>
          {(group) => {
            const open = () => !collapsedGroups().includes(group.label)
            const visible = () =>
              props.compact || open()
                ? group.items
                : group.items.filter((item) => isActive(item.href))
            const listId = `nav-${group.label.replace(/\s+/g, '-')}`
            return (
              <div class="grid gap-0.5">
                <Show
                  when={!props.compact}
                  fallback={<div class="mx-2 mb-1 border-t border-border" aria-hidden="true" />}
                >
                  <button
                    type="button"
                    aria-expanded={open()}
                    aria-controls={listId}
                    onClick={() => toggleGroup(group.label)}
                    class="group/heading flex items-center gap-1 rounded-control px-2 pb-1 text-left text-caption font-medium text-text-subtle hover:text-text focus-visible:outline-2 focus-visible:outline-accent"
                  >
                    <span class="flex-1">{group.label}</span>
                    <IconChevronDown
                      class={cn(
                        'size-3.5 opacity-0 transition-[transform,opacity] duration-(--duration-press) group-hover/heading:opacity-100 group-focus-visible/heading:opacity-100',
                        !open() && '-rotate-90 opacity-100',
                      )}
                      stroke-width={2}
                      aria-hidden="true"
                    />
                  </button>
                </Show>
                <div id={listId} class="grid gap-0.5">
                  <For each={visible()}>
                    {(item) => (
                      <NavLink item={item} active={isActive(item.href)} compact={props.compact} />
                    )}
                  </For>
                </div>
              </div>
            )
          }}
        </For>
      </nav>

      <UserMenu compact={props.compact} />
    </div>
  )
}

const routeAllowed = (path: string) => {
  const actor = session()?.actor
  if (!actor) return false
  if (path === '/prototype/home') return true
  if (path === '/prototype' || path.startsWith('/prototype/inbox'))
    return can(actor, 'read', 'tasks')
  const item = allNavItems()
    .filter((entry) => path === entry.href || path.startsWith(`${entry.href}/`))
    .sort((a, b) => b.href.length - a.href.length)[0]
  return item ? can(actor, item.action ?? 'read', item.resource) : true
}

export const Shell = (props: ParentProps) => {
  const location = useLocation()
  useInboxNews()
  onMount(() => {
    const onKey = (event: KeyboardEvent) => {
      const mod = event.metaKey || event.ctrlKey
      if (mod && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        setPaletteOpen((open) => !open)
      }
      const typing =
        event.target instanceof HTMLElement &&
        (event.target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(event.target.tagName))
      if (
        mod &&
        event.key.toLowerCase() === 'j' &&
        (!typing || location.pathname === `${BASE}/home`)
      ) {
        event.preventDefault()
        if (location.pathname === `${BASE}/home`) openAssistant()
        else setAssistantOpen((open) => !open)
      }
    }
    document.addEventListener('keydown', onKey)
    onCleanup(() => document.removeEventListener('keydown', onKey))
  })

  return (
    <div data-proto class="flex min-h-[100dvh] bg-app text-text lg:h-[100dvh]">
      <a
        href="#proto-main"
        class="sr-only z-50 rounded-control bg-surface px-3 py-2 text-body focus:not-sr-only focus:fixed focus:top-2 focus:left-2"
      >
        Перейти до вмісту
      </a>
      <aside
        class={cn(
          'sticky top-0 hidden h-[100dvh] shrink-0 px-3 py-3 lg:block',
          'transition-[width] duration-(--duration-overlay) ease-(--ease-overlay)',
          collapsed() ? 'w-16 px-2' : 'w-(--size-sidebar)',
        )}
      >
        <SidebarContent compact={collapsed()} />
      </aside>

      <Sheet
        open={mobileNavOpen()}
        onOpenChange={setMobileNavOpen}
        title="Меню"
        class="lg:hidden"
        bodyClass="p-3"
      >
        <SidebarContent compact={false} />
      </Sheet>

      <div class="flex min-w-0 flex-1 flex-col lg:min-h-0 lg:py-2 lg:pr-2">
        <div class="flex min-h-[100dvh] min-w-0 flex-1 flex-col bg-surface lg:min-h-0 lg:h-[calc(100dvh-1rem)] lg:rounded-panel lg:border lg:border-border lg:shadow-[0_1px_2px_oklch(0.196_0.007_265/0.04)]">
          <Show
            when={routeAllowed(location.pathname)}
            fallback={
              <div class="grid flex-1 place-content-center gap-2 p-6 text-center">
                <h1 class="font-display text-title">Немає доступу</h1>
                <p class="text-body text-text-muted">Цей розділ недоступний для вашої ролі.</p>
                <A href="/prototype/inbox" class="text-body underline">
                  До моїх завдань
                </A>
              </div>
            }
          >
            {props.children}
          </Show>
        </div>
      </div>

      <CommandSearch
        open={paletteOpen()}
        onClose={() => setPaletteOpen(false)}
        onAsk={() => {
          setPaletteOpen(false)
          openAssistant()
        }}
      />
      <AssistantDock
        open={assistantOpen() && location.pathname !== `${BASE}/home`}
        onOpenChange={setAssistantOpen}
      />
      <PushSettings open={pushOpen()} onOpenChange={setPushOpen} />
    </div>
  )
}

export interface Crumb {
  label: string
  href?: string
}

/**
 * One page inside the shell. The header sits outside the scroll container so long tables never
 * repaint a sticky bar, and breadcrumbs are real links back up the hierarchy.
 */
export const Page = (
  props: ParentProps<{
    crumbs: Crumb[]
    tabs?: JSX.Element
    actions?: JSX.Element
    bodyClass?: string
  }>,
) => (
  <>
    <header class="flex min-h-12 shrink-0 flex-wrap items-center gap-x-3 gap-y-2 border-b border-border px-4 py-2 lg:px-5">
      <button
        type="button"
        onClick={() => setMobileNavOpen(true)}
        aria-label={
          newInboxCount() > 0
            ? `Відкрити меню, нових завдань: ${formatCount(newInboxCount())}`
            : 'Відкрити меню'
        }
        class="relative -ml-1 grid size-(--size-touch) place-items-center rounded-control text-text-muted hover:bg-surface-sunken lg:hidden"
      >
        <IconMenu2 class="size-5" stroke-width={1.5} aria-hidden="true" />
        <Show when={newInboxCount() > 0}>
          <span
            data-mobile-new-badge
            aria-hidden="true"
            class="absolute top-1 right-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-accent px-1 font-mono text-micro font-semibold text-text-inverse tabular-nums"
          >
            {formatCount(newInboxCount())}
          </span>
        </Show>
      </button>
      <nav aria-label="Навігаційний ланцюжок" class="flex min-w-0 items-center gap-1.5 text-ui">
        <For each={props.crumbs}>
          {(crumb, index) => (
            <>
              <Show when={index() > 0}>
                <IconChevronRight
                  class="size-3.5 shrink-0 text-text-subtle"
                  stroke-width={1.5}
                  aria-hidden="true"
                />
              </Show>
              <Show
                when={crumb.href && index() < props.crumbs.length - 1}
                fallback={
                  <h1 class="truncate font-semibold text-text" aria-current="page">
                    {crumb.label}
                  </h1>
                }
              >
                <A
                  href={crumb.href ?? '#'}
                  class="truncate text-text-subtle transition-colors duration-(--duration-press) hover:text-text focus-visible:outline-2 focus-visible:outline-accent"
                >
                  {crumb.label}
                </A>
              </Show>
            </>
          )}
        </For>
      </nav>
      <Show when={props.tabs}>
        <div class="flex min-w-0 items-center">{props.tabs}</div>
      </Show>
      <div class="ml-auto flex min-w-0 max-w-full flex-wrap items-center justify-end gap-2">
        {props.actions}
      </div>
    </header>
    <main id="proto-main" class={cn('min-h-0 flex-1 overflow-auto', props.bodyClass)}>
      {props.children}
    </main>
  </>
)
